import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ListProjectsResult, Project } from '@terafile/shared'
import { type ReactNode, useState } from 'react'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { TRASH_ACTION } from '../lib/labels.js'
import { archiveProject, updateProject } from '../lib/projects-api.js'
import { folderIdFromDropId, isTrashDropId } from '../lib/projects-location.js'
import { useToast } from '../lib/toast.js'
import { MoveToTrashModal, type TrashTarget } from './MoveToTrashModal.js'

type DragProjectData = {
  type: 'project'
  name: string
  folderId: string | null
}

/**
 * テナントシェル全体の DnD。メインのプロジェクト行 → サイドバー場所（フォルダ／未分類／ゴミ箱）。
 */
export function ProjectsDndShell({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [trashTarget, setTrashTarget] = useState<TrashTarget | null>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    queryClient.invalidateQueries({ queryKey: ['folders'] })
  }

  const moveMutation = useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      updateProject(id, { folderId }),
    onSuccess: () => {
      invalidate()
      toast.success('移動しました')
    },
    onError: (err) => toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK)),
  })

  const archiveMutation = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => archiveProject(id),
    onSuccess: (_, variables) => {
      invalidate()
      toast.success(`「${variables.name}」を${TRASH_ACTION.toastMovedProject}`)
      setTrashTarget(null)
    },
    onError: (err) => {
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  const onDragEnd = (event: DragEndEvent) => {
    const overId = event.over?.id
    if (overId === undefined) return
    const projectId = String(event.active.id)
    const dragData = event.active.data.current as DragProjectData | undefined
    const projectName = dragData?.name ?? resolveProjectName(queryClient, projectId)
    const currentFolderId = dragData?.folderId ?? resolveProjectFolderId(queryClient, projectId)

    if (isTrashDropId(String(overId))) {
      setTrashTarget({ kind: 'project', id: projectId, name: projectName })
      return
    }

    const folderId = folderIdFromDropId(String(overId))
    if (folderId === undefined) return
    if (currentFolderId === folderId) return
    moveMutation.mutate({ id: projectId, folderId })
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      {children}
      <MoveToTrashModal
        target={trashTarget}
        onClose={() => setTrashTarget(null)}
        onConfirm={() => {
          if (trashTarget?.kind === 'project') {
            archiveMutation.mutate({ id: trashTarget.id, name: trashTarget.name })
          }
        }}
        isPending={archiveMutation.isPending}
      />
    </DndContext>
  )
}

function resolveProjectName(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
): string {
  for (const [, data] of queryClient.getQueriesData<ListProjectsResult>({
    queryKey: ['projects'],
  })) {
    const hit = data?.items?.find((p) => p.id === projectId)
    if (hit) return hit.name
  }
  const detail = queryClient.getQueryData<Project>(['projects', projectId])
  return detail?.name ?? 'プロジェクト'
}

function resolveProjectFolderId(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
): string | null | undefined {
  for (const [, data] of queryClient.getQueriesData<ListProjectsResult>({
    queryKey: ['projects'],
  })) {
    const hit = data?.items?.find((p) => p.id === projectId)
    if (hit) return hit.folderId
  }
  const detail = queryClient.getQueryData<Project>(['projects', projectId])
  return detail?.folderId
}
